/**
 * ReviewTranscriptPanel - 文字起こし（左）+ 指摘一覧（右）の2カラム
 *
 * ManagerReviewView の review モード Step 3 相当を、単一セッション向けに切り出したもの。
 * 指摘 ⇔ 文字起こしハイライトの連動（主発言=赤、補助発言=amber、選択中のみ補助を光らせる）を実装。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { env } from '@/lib/env';
import {
  TranscriptTimeline,
  type TranscriptTimelineHandle,
} from '@/components/review/TranscriptTimeline';
import {
  reviewTranscriptBlocksToTimeline,
  type ReviewAdditionalFeedback,
  type ReviewFeedbackItem,
  type ReviewItemJudgmentValue,
  type ReviewSessionDetailResponse,
  type Utterance,
} from '@/types/review';
import {
  normalizeReviewDisplayText,
  reviewStatusDisplayLabel,
  speakerDisplayLabel,
} from '@/utils/reviewDisplayLabels';

interface ItemReviewDraft {
  judgment?: ReviewItemJudgmentValue;
  comment: string;
}

type ReviewCard =
  | { type: 'ai'; item: ReviewFeedbackItem; order: number }
  | { type: 'manual'; item: ReviewAdditionalFeedback; order: number };

type AdditionalFeedbackCreateInput = Omit<
  ReviewAdditionalFeedback,
  'id' | 'author_name' | 'created_at' | 'can_delete' | 'can_edit' | 'reviewer_user_id'
>;

interface ReviewTranscriptPanelProps {
  detail: ReviewSessionDetailResponse;
  showReviewerJudgments?: boolean;
  reviewDrafts?: Record<string, ItemReviewDraft>;
  canEditReviews?: boolean;
  onItemJudgmentChange?: (feedbackKey: string, judgment: ReviewItemJudgmentValue) => void;
  onItemCommentChange?: (feedbackKey: string, comment: string) => void;
  onItemCommentCommit?: (feedbackKey: string) => void;
  additionalFeedbacks?: ReviewAdditionalFeedback[];
  onAdditionalFeedbacksChange?: (feedbacks: ReviewAdditionalFeedback[]) => void;
  onAdditionalFeedbackCreate?: (
    feedback: AdditionalFeedbackCreateInput,
  ) => Promise<ReviewAdditionalFeedback> | ReviewAdditionalFeedback;
  onAdditionalFeedbackUpdate?: (
    feedbackId: string,
    commentBody: string,
  ) => Promise<ReviewAdditionalFeedback> | ReviewAdditionalFeedback;
  onAdditionalFeedbackDelete?: (feedbackId: string) => Promise<void> | void;
  showAdditionalFeedback?: boolean;
  /** AIフィードバック検知カードを表示するか。既定は env.VITE_SHOW_AI_FEEDBACK（可逆スイッチ）。 */
  showAiFeedback?: boolean;
  /** 発話クリック時に呼ばれる。音声ロード済みなら親で seekToUtterance を渡す。 */
  onUtteranceClick?: (utteranceId: string) => void;
  /** 音声再生位置に対応する発話。選択中発話とは別に表示だけ追従する。 */
  activeAudioUtteranceId?: string | null;
  isAudioPlaying?: boolean;
  followAudioPlayback?: boolean;
  onFollowAudioPlaybackChange?: (enabled: boolean) => void;
}

function JudgmentBadge({ judgment }: { judgment: string }) {
  const isNg = judgment === 'NG';
  const label = isNg ? 'コーチにフィードバック' : '誤検知';
  return (
    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${
      isNg ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
    }`}>
      {label}
    </span>
  );
}

function judgmentDisplayLabel(judgment: ReviewItemJudgmentValue) {
  return judgment === 'NG' ? 'コーチにフィードバック' : '誤検知';
}

export function ReviewTranscriptPanel({
  detail,
  showReviewerJudgments = false,
  reviewDrafts = {},
  canEditReviews = false,
  onItemJudgmentChange,
  onItemCommentChange,
  onItemCommentCommit,
  additionalFeedbacks = [],
  onAdditionalFeedbacksChange,
  onAdditionalFeedbackCreate,
  onAdditionalFeedbackUpdate,
  onAdditionalFeedbackDelete,
  showAdditionalFeedback = false,
  showAiFeedback = env.VITE_SHOW_AI_FEEDBACK,
  onUtteranceClick,
  activeAudioUtteranceId = null,
  isAudioPlaying = false,
  followAudioPlayback = false,
  onFollowAudioPlaybackChange,
}: ReviewTranscriptPanelProps) {
  const [selectedFeedbackKey, setSelectedFeedbackKey] = useState<string | null>(
    showAiFeedback ? (detail.session_feedback_items[0]?.feedback_key ?? null) : null,
  );
  const [selectedAdditionalFeedbackId, setSelectedAdditionalFeedbackId] = useState<string | null>(null);
  const [selectedUtteranceId, setSelectedUtteranceId] = useState<string | undefined>(
    showAiFeedback
      ? (detail.session_feedback_items[0]?.primary_utterance_id ??
        detail.session_feedback_items[0]?.utterance_id ??
        undefined)
      : undefined,
  );
  const [isAddingAdditionalFeedback, setIsAddingAdditionalFeedback] = useState(false);
  const [additionalDraftText, setAdditionalDraftText] = useState('');
  const [additionalActionError, setAdditionalActionError] = useState<string | null>(null);
  const [isSubmittingAdditionalFeedback, setIsSubmittingAdditionalFeedback] = useState(false);
  const [editingAdditionalFeedbackId, setEditingAdditionalFeedbackId] = useState<string | null>(null);
  const [additionalEditDraftText, setAdditionalEditDraftText] = useState('');
  const [updatingAdditionalFeedbackId, setUpdatingAdditionalFeedbackId] = useState<string | null>(null);
  const [removedAdditionalFeedbackIds, setRemovedAdditionalFeedbackIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [updatedAdditionalFeedbacks, setUpdatedAdditionalFeedbacks] = useState<
    Map<string, ReviewAdditionalFeedback>
  >(() => new Map());

  const timelineRef = useRef<TranscriptTimelineHandle>(null);

  // 時系列順 feedback
  const feedbackItems = useMemo<ReviewFeedbackItem[]>(() => {
    // AIフィードバック非表示モードでは検知カードを一切出さない（手動FBは別管理で残る）
    if (!showAiFeedback) return [];
    return [...detail.session_feedback_items].sort((a, b) => {
      const ai = a.sequence_index ?? 0;
      const bi = b.sequence_index ?? 0;
      if (ai !== bi) return ai - bi;
      return a.timestamp.localeCompare(b.timestamp);
    });
  }, [detail.session_feedback_items, showAiFeedback]);

  const timelineBlocks = useMemo(
    // AIフィードバック非表示モードでは、左タイムラインのハイライト/番号バッジも出さない
    () =>
      reviewTranscriptBlocksToTimeline(
        detail.transcript_blocks,
        showAiFeedback ? detail.session_feedback_items : [],
      ),
    [detail.transcript_blocks, detail.session_feedback_items, showAiFeedback],
  );

  const additionalItems = useMemo<ReviewAdditionalFeedback[]>(
    () =>
      [...(detail.additional_feedbacks ?? []), ...additionalFeedbacks].filter(
        (item) => !removedAdditionalFeedbackIds.has(item.id),
      ).map((item) => updatedAdditionalFeedbacks.get(item.id) ?? item),
    [
      detail.additional_feedbacks,
      additionalFeedbacks,
      removedAdditionalFeedbackIds,
      updatedAdditionalFeedbacks,
    ],
  );

  const selectedUtterance = useMemo(() => {
    if (!selectedUtteranceId) return null;
    for (const block of detail.transcript_blocks) {
      const match = block.utterances.find((utterance) => utterance.utterance_id === selectedUtteranceId);
      if (match) return { ...match, block_name: match.block_name || block.name };
    }
    return null;
  }, [detail.transcript_blocks, selectedUtteranceId]);

  // utterance_id → ReviewFeedbackItem[]（primary/supporting 双方を含む。発話クリック→カード選択）
  const feedbackByUtterance = useMemo(() => {
    const map = new Map<string, ReviewFeedbackItem[]>();
    const pushUnique = (utteranceId: string, item: ReviewFeedbackItem) => {
      const existing = map.get(utteranceId);
      if (!existing) {
        map.set(utteranceId, [item]);
        return;
      }
      if (!existing.some((e) => e.feedback_key === item.feedback_key)) existing.push(item);
    };
    for (const item of feedbackItems) {
      const primary = item.primary_utterance_id || item.utterance_id;
      if (primary) pushUnique(primary, item);
      for (const supportingId of item.supporting_utterance_ids) {
        if (!supportingId || supportingId === primary) continue;
        pushUnique(supportingId, item);
      }
    }
    return map;
  }, [feedbackItems]);

  const additionalFeedbackByUtterance = useMemo(() => {
    const map = new Map<string, ReviewAdditionalFeedback[]>();
    for (const item of additionalItems) {
      const existing = map.get(item.utterance_id) ?? [];
      existing.push(item);
      map.set(item.utterance_id, existing);
    }
    return map;
  }, [additionalItems]);

  // 選択中 feedback の supporting_utterance_ids（Set 化: includes を避けパフォーマンス向上）
  const activeSupportingUtteranceIds = useMemo<Set<string>>(() => {
    if (!selectedFeedbackKey) return new Set();
    const match = feedbackItems.find((item) => item.feedback_key === selectedFeedbackKey);
    return new Set(match?.supporting_utterance_ids ?? []);
  }, [selectedFeedbackKey, feedbackItems]);

  // feedback_key → 1-indexed 番号
  const feedbackIndexByKey = useMemo(() => {
    const map = new Map<string, number>();
    feedbackItems.forEach((item, i) => map.set(item.feedback_key, i + 1));
    return map;
  }, [feedbackItems]);

  // utterance_id → 指摘番号配列（primary / supporting 双方から番号を拾う）
  const feedbackIndicesByUtteranceId = useMemo(() => {
    const map = new Map<string, number[]>();
    const push = (utteranceId: string, n: number) => {
      const existing = map.get(utteranceId) ?? [];
      if (!existing.includes(n)) existing.push(n);
      map.set(utteranceId, existing);
    };
    feedbackItems.forEach((item, i) => {
      const n = i + 1;
      const primary = item.primary_utterance_id || item.utterance_id;
      if (primary) push(primary, n);
      for (const supportingId of item.supporting_utterance_ids) {
        if (!supportingId || supportingId === primary) continue;
        push(supportingId, n);
      }
    });
    return map;
  }, [feedbackItems]);

  const reviewCards = useMemo<ReviewCard[]>(() => {
    const aiCards: ReviewCard[] = feedbackItems.map((item, index) => ({
      type: 'ai',
      item,
      order: item.sequence_index ?? index,
    }));
    const manualCards: ReviewCard[] = additionalItems.map((item, index) => ({
      type: 'manual',
      item,
      order: item.sequence_index ?? Number.MAX_SAFE_INTEGER - additionalItems.length + index,
    }));
    return [...aiCards, ...manualCards].sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      const aTime = a.type === 'ai' ? a.item.timestamp : a.item.timestamp;
      const bTime = b.type === 'ai' ? b.item.timestamp : b.item.timestamp;
      return aTime.localeCompare(bTime);
    });
  }, [additionalItems, feedbackItems]);

  // session 切替時の選択状態リセット（同 instance に別 detail が入ったとき旧 key が残る問題対策）
  const sessionId = detail.session.session_id;
  useEffect(() => {
    const first = feedbackItems[0];
    setSelectedFeedbackKey(first?.feedback_key ?? null);
    setSelectedAdditionalFeedbackId(null);
    setSelectedUtteranceId(first?.primary_utterance_id ?? first?.utterance_id ?? undefined);
    setIsAddingAdditionalFeedback(false);
    setAdditionalDraftText('');
    setAdditionalActionError(null);
    setIsSubmittingAdditionalFeedback(false);
    setRemovedAdditionalFeedbackIds(new Set());
    setEditingAdditionalFeedbackId(null);
    setAdditionalEditDraftText('');
    setUpdatingAdditionalFeedbackId(null);
    setUpdatedAdditionalFeedbacks(new Map());
    // sessionId が変わった時だけ実行（feedbackItems は同 session 内で都度 sort される）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (!followAudioPlayback || !isAudioPlaying || !activeAudioUtteranceId) return;
    timelineRef.current?.scrollToUtterance(activeAudioUtteranceId);
  }, [activeAudioUtteranceId, followAudioPlayback, isAudioPlaying]);

  const handleFeedbackCardClick = useCallback((item: ReviewFeedbackItem) => {
    setSelectedFeedbackKey(item.feedback_key);
    setSelectedAdditionalFeedbackId(null);
    const targetUtteranceId = item.primary_utterance_id || item.utterance_id;
    if (targetUtteranceId) {
      setSelectedUtteranceId(targetUtteranceId);
      timelineRef.current?.scrollToUtterance(targetUtteranceId);
    }
  }, []);

  const handleAdditionalCardClick = useCallback((item: ReviewAdditionalFeedback) => {
    setSelectedFeedbackKey(null);
    setSelectedAdditionalFeedbackId(item.id);
    setSelectedUtteranceId(item.utterance_id);
    timelineRef.current?.scrollToUtterance(item.utterance_id);
  }, []);

  const handleUtteranceSelect = useCallback(
    (utterance: Utterance) => {
      setSelectedUtteranceId(utterance.id);
      const matches = feedbackByUtterance.get(utterance.id);
      if (matches && matches.length > 0) {
        setSelectedFeedbackKey(matches[0]!.feedback_key);
        setSelectedAdditionalFeedbackId(null);
      } else {
        const additionalMatches = additionalFeedbackByUtterance.get(utterance.id);
        setSelectedFeedbackKey(null);
        setSelectedAdditionalFeedbackId(additionalMatches?.[0]?.id ?? null);
      }
      onUtteranceClick?.(utterance.id);
    },
    [additionalFeedbackByUtterance, feedbackByUtterance, onUtteranceClick],
  );

  const handleStartAdditionalFeedback = useCallback(() => {
    setIsAddingAdditionalFeedback(true);
    setEditingAdditionalFeedbackId(null);
    setAdditionalEditDraftText('');
    setAdditionalActionError(null);
  }, []);

  const handleAddAdditionalFeedback = useCallback(async () => {
    const comment = additionalDraftText.trim();
    if (!comment || !selectedUtterance || isSubmittingAdditionalFeedback) return;
    setIsSubmittingAdditionalFeedback(true);
    setAdditionalActionError(null);
    const draft: AdditionalFeedbackCreateInput = {
      utterance_id: selectedUtterance.utterance_id,
      sequence_index: selectedUtterance.sequence_index,
      timestamp: selectedUtterance.timestamp,
      speaker: selectedUtterance.speaker,
      block_name: selectedUtterance.block_name,
      quote: selectedUtterance.text,
      comment_body: comment,
    };
    try {
      const next = onAdditionalFeedbackCreate
        ? await onAdditionalFeedbackCreate(draft)
        : {
            ...draft,
            id: `local-additional-${Date.now()}`,
            author_name: 'Preview Admin',
            created_at: 'UI preview',
            can_delete: true,
            can_edit: true,
          };
      onAdditionalFeedbacksChange?.([...additionalFeedbacks, next]);
      setAdditionalDraftText('');
      setIsAddingAdditionalFeedback(false);
      setSelectedFeedbackKey(null);
      setSelectedAdditionalFeedbackId(next.id);
      setEditingAdditionalFeedbackId(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : '追加コメントの保存に失敗しました';
      setAdditionalActionError(message);
    } finally {
      setIsSubmittingAdditionalFeedback(false);
    }
  }, [
    additionalDraftText,
    additionalFeedbacks,
    isSubmittingAdditionalFeedback,
    onAdditionalFeedbackCreate,
    onAdditionalFeedbacksChange,
    selectedUtterance,
  ]);

  const handleRemoveAdditionalFeedback = useCallback(
    async (id: string) => {
      const confirmed = window.confirm?.('この追加コメントを削除しますか？') ?? true;
      if (!confirmed) return;
      setAdditionalActionError(null);
      try {
        await onAdditionalFeedbackDelete?.(id);
        onAdditionalFeedbacksChange?.(additionalFeedbacks.filter((item) => item.id !== id));
        setRemovedAdditionalFeedbackIds((current) => {
          const next = new Set(current);
          next.add(id);
          return next;
        });
        if (selectedAdditionalFeedbackId === id) setSelectedAdditionalFeedbackId(null);
      } catch (error) {
        const message = error instanceof Error ? error.message : '追加コメントの削除に失敗しました';
        setAdditionalActionError(message);
      }
    },
    [additionalFeedbacks, onAdditionalFeedbackDelete, onAdditionalFeedbacksChange, selectedAdditionalFeedbackId],
  );

  const handleStartEditAdditionalFeedback = useCallback((item: ReviewAdditionalFeedback) => {
    setSelectedFeedbackKey(null);
    setSelectedAdditionalFeedbackId(item.id);
    setSelectedUtteranceId(item.utterance_id);
    setIsAddingAdditionalFeedback(false);
    setAdditionalActionError(null);
    setEditingAdditionalFeedbackId(item.id);
    setAdditionalEditDraftText(item.comment_body);
  }, []);

  const handleCancelEditAdditionalFeedback = useCallback(() => {
    setEditingAdditionalFeedbackId(null);
    setAdditionalEditDraftText('');
    setAdditionalActionError(null);
  }, []);

  const handleUpdateAdditionalFeedback = useCallback(
    async (item: ReviewAdditionalFeedback) => {
      const comment = additionalEditDraftText.trim();
      if (!comment || updatingAdditionalFeedbackId) return;
      setUpdatingAdditionalFeedbackId(item.id);
      setAdditionalActionError(null);
      try {
        const next = onAdditionalFeedbackUpdate
          ? await onAdditionalFeedbackUpdate(item.id, comment)
          : { ...item, comment_body: comment };
        setUpdatedAdditionalFeedbacks((current) => {
          const updated = new Map(current);
          updated.set(next.id, next);
          return updated;
        });
        onAdditionalFeedbacksChange?.(
          additionalFeedbacks.map((feedback) => (feedback.id === next.id ? next : feedback)),
        );
        setEditingAdditionalFeedbackId(null);
        setAdditionalEditDraftText('');
      } catch (error) {
        const message = error instanceof Error ? error.message : '追加コメントの更新に失敗しました';
        setAdditionalActionError(message);
      } finally {
        setUpdatingAdditionalFeedbackId(null);
      }
    },
    [
      additionalEditDraftText,
      additionalFeedbacks,
      onAdditionalFeedbacksChange,
      onAdditionalFeedbackUpdate,
      updatingAdditionalFeedbackId,
    ],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm xl:flex-row">
      {/* 左: 文字起こしタイムライン */}
      <div className="flex min-w-0 flex-[1_1_0%] flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-gray-600">
            <span className="font-semibold text-gray-900">
              {detail.session.display_name || detail.session.session_id}
            </span>
            {detail.session.client_name && detail.session.client_name !== detail.session.display_name && (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                {detail.session.client_name}
              </span>
            )}
            <span className="text-xs text-gray-500">
              {detail.session.day}
              {detail.session.day === 'Day1' && detail.session.day1_variant === 'first' ? '（初回）' : ''}
              {detail.session.day === 'Day1' && detail.session.day1_variant === 'repeat' ? '（複数回目）' : ''}
              {detail.session.date ? ` / ${detail.session.date}` : ''}
            </span>
          </div>
          {onFollowAudioPlaybackChange && (
            <button
              type="button"
              onClick={() => onFollowAudioPlaybackChange(!followAudioPlayback)}
              aria-pressed={followAudioPlayback}
              aria-label={
                followAudioPlayback
                  ? '再生位置への自動スクロールをオフ'
                  : '再生位置への自動スクロールをオン'
              }
              title={
                followAudioPlayback
                  ? '再生位置への自動スクロールをオフ'
                  : '再生位置への自動スクロールをオン'
              }
              className={`shrink-0 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${
                followAudioPlayback
                  ? 'border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100'
                  : 'border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
            >
              音声に合わせてスクロール：{followAudioPlayback ? 'ON' : 'OFF'}
            </button>
          )}
        </div>

        <TranscriptTimeline
          ref={timelineRef}
          blocks={timelineBlocks}
          selectedUtteranceId={selectedUtteranceId}
          activeUtteranceId={activeAudioUtteranceId}
          viewMode="full"
          onUtteranceSelect={handleUtteranceSelect}
          supportingUtteranceIdsWhitelist={activeSupportingUtteranceIds}
          feedbackIndicesByUtteranceId={feedbackIndicesByUtteranceId}
        />
      </div>

      {/* 右: フィードバック検知一覧 */}
      <aside className="flex max-h-80 w-full flex-shrink-0 flex-col overflow-hidden border-t border-gray-200 bg-white xl:max-h-none xl:w-[clamp(22rem,34vw,36rem)] xl:border-l xl:border-t-0">
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-700">{showAiFeedback ? 'フィードバック検知一覧' : 'フィードバック一覧'}</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{reviewCards.length} 件</span>
            {showAdditionalFeedback && canEditReviews && (
              <button
                type="button"
                onClick={handleStartAdditionalFeedback}
                className="rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:border-blue-300 hover:bg-blue-100"
              >
                手動フィードバック追加
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 space-y-2 overflow-y-auto p-3">
          {showAdditionalFeedback && canEditReviews && isAddingAdditionalFeedback && (
            <div className="sticky top-0 z-20 rounded-lg border border-blue-300 bg-blue-50 p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div>
                  <h4 className="text-sm font-semibold text-gray-800">追加コメント</h4>
                  <p className="mt-0.5 text-xs text-gray-500">
                    左の文字起こしから発言を選び、AIフィードバックとは別のコメントを追加します。
                  </p>
                </div>
              </div>
              {selectedUtterance ? (
                <div className="mb-2 rounded-md border border-blue-100 bg-white p-2">
                  <div className="mb-1 flex items-center gap-2 text-[11px] text-gray-500">
                    <span className="font-mono">{selectedUtterance.timestamp}</span>
                    <span>{speakerDisplayLabel(selectedUtterance.speaker)}</span>
                    <span>{selectedUtterance.block_name}</span>
                  </div>
                  <p className="line-clamp-2 text-xs leading-relaxed text-gray-700">
                    「{selectedUtterance.text}」
                  </p>
                </div>
              ) : (
                <p className="mb-2 rounded-md bg-white p-2 text-xs text-gray-400">
                  コメントを追加する発言を選択してください
                </p>
              )}
              <textarea
                placeholder="この発言への追加コメント"
                value={additionalDraftText}
                onChange={(e) => setAdditionalDraftText(e.target.value)}
                className="w-full resize-none rounded-md border border-blue-200 bg-white p-2 text-xs leading-relaxed focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows={3}
              />
              {additionalActionError && (
                <p className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                  {additionalActionError}
                </p>
              )}
              <div className="mt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingAdditionalFeedback(false);
                    setAdditionalDraftText('');
                    setAdditionalActionError(null);
                  }}
                  disabled={isSubmittingAdditionalFeedback}
                  className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  onClick={handleAddAdditionalFeedback}
                  disabled={!selectedUtterance || !additionalDraftText.trim() || isSubmittingAdditionalFeedback}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmittingAdditionalFeedback ? '追加中...' : '追加'}
                </button>
              </div>
            </div>
          )}
          {additionalActionError && !isAddingAdditionalFeedback && (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {additionalActionError}
            </p>
          )}
          {reviewCards.map((card) => {
            if (card.type === 'manual') {
              const item = card.item;
              const isSelected = item.id === selectedAdditionalFeedbackId;
              const isEditing = item.id === editingAdditionalFeedbackId;
              const canEditAdditional = canEditReviews && item.can_edit === true;
              const canDeleteAdditional = canEditReviews && item.can_delete === true;
              const isUpdating = updatingAdditionalFeedbackId === item.id;
              return (
                <div
                  key={item.id}
                  className={`rounded-lg border transition-all ${
                    isSelected
                      ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-100'
                      : 'border-blue-200 bg-white hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 p-3 pb-1.5">
                    <button
                      type="button"
                      onClick={() => handleAdditionalCardClick(item)}
                      className="flex min-w-0 flex-wrap items-center gap-2 text-left text-xs text-gray-500"
                    >
                      <span className="rounded border border-blue-200 bg-blue-100 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700">
                        追加コメント
                      </span>
                      <span className="font-mono">{item.timestamp}</span>
                      <span>{speakerDisplayLabel(item.speaker)}</span>
                      {item.author_name && (
                        <span className="font-medium text-gray-700">{item.author_name}</span>
                      )}
                    </button>
                    {(canEditAdditional || canDeleteAdditional) && (
                      <div className="flex flex-shrink-0 items-center gap-1">
                        {canEditAdditional && (
                          <button
                            type="button"
                            onClick={() => handleStartEditAdditionalFeedback(item)}
                            className="rounded border border-blue-200 bg-white px-2 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-50"
                          >
                            編集
                          </button>
                        )}
                        {canDeleteAdditional && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAdditionalFeedback(item.id)}
                            className="rounded border border-red-200 bg-white px-2 py-1 text-[11px] font-semibold text-red-600 hover:bg-red-50"
                          >
                            削除
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {isEditing ? (
                    <div className="px-3 pb-3">
                      <p className="mb-2 text-sm leading-snug text-gray-800 line-clamp-2">
                        「{item.quote}」
                      </p>
                      <textarea
                        aria-label="追加コメント本文"
                        value={additionalEditDraftText}
                        onChange={(e) => setAdditionalEditDraftText(e.target.value)}
                        className="w-full resize-none rounded-md border border-blue-200 bg-white p-2 text-xs leading-relaxed focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        rows={3}
                      />
                      <div className="mt-2 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={handleCancelEditAdditionalFeedback}
                          disabled={isUpdating}
                          className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                        >
                          キャンセル
                        </button>
                        <button
                          type="button"
                          onClick={() => handleUpdateAdditionalFeedback(item)}
                          disabled={!additionalEditDraftText.trim() || isUpdating}
                          className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isUpdating ? '保存中...' : '保存'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleAdditionalCardClick(item)}
                      className="w-full px-3 pb-3 text-left"
                    >
                      <p className={`mb-2 text-sm leading-snug text-gray-800 ${
                        isSelected ? 'whitespace-pre-wrap break-words' : 'line-clamp-2'
                      }`}>
                        「{item.quote}」
                      </p>
                      <p className={`text-xs leading-snug text-blue-800 ${
                        isSelected ? 'whitespace-pre-wrap break-words' : 'line-clamp-3'
                      }`}>
                        {normalizeReviewDisplayText(item.comment_body)}
                      </p>
                    </button>
                  )}
                </div>
              );
            }
            const item = card.item;
            const isSelected = item.feedback_key === selectedFeedbackKey;
            const displayName = normalizeReviewDisplayText(item.item_name || item.item_id);
            const statusLabel = ['ACTION', 'ISSUE', 'CHECK'].includes(item.status)
              ? null
              : reviewStatusDisplayLabel(item.status);
            const indexNumber = feedbackIndexByKey.get(item.feedback_key);
            const judgments = item.reviewer_judgments ?? [];
            const draft = reviewDrafts[item.feedback_key] ?? { comment: '' };
            const hasDisagreement =
              new Set(judgments.map((j) => j.judgment)).size > 1;
            return (
              <div
                key={item.feedback_key}
                className={`rounded-lg border transition-all ${
                  isSelected
                    ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-100'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <button
                  type="button"
                  onClick={() => handleFeedbackCardClick(item)}
                  className="w-full p-3 text-left"
                >
                  <div className="mb-1.5 flex min-w-0 flex-wrap items-center gap-2 text-xs text-gray-500">
                    <span className="rounded border border-red-200 bg-red-100 px-1.5 py-0.5 text-[11px] font-semibold text-red-700">
                      AIフィードバック
                    </span>
                    {indexNumber !== undefined && (
                      <span className="rounded border border-red-200 bg-red-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-red-700">
                        #{indexNumber}
                      </span>
                    )}
                    <span className="font-mono">{item.timestamp}</span>
                    <span>{speakerDisplayLabel(item.speaker)}</span>
                    {displayName && (
                      <span className="min-w-0 font-medium text-gray-700">{displayName}</span>
                    )}
                    {statusLabel && (
                      <span className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] font-semibold text-gray-600">
                        {statusLabel}
                      </span>
                    )}
                    {showReviewerJudgments && hasDisagreement && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-700">
                        判定不一致
                      </span>
                    )}
                  </div>
                  {item.quote && (
                    <p
                      className={`mb-2 text-sm leading-snug text-gray-800 ${
                        isSelected ? 'whitespace-pre-wrap break-words' : 'line-clamp-2'
                      }`}
                    >
                      「{item.quote}」
                    </p>
                  )}
                  {item.recommendation && (
                    <p
                      className={`text-xs leading-snug text-blue-700 ${
                        isSelected ? 'whitespace-pre-wrap break-words' : 'line-clamp-3'
                      }`}
                    >
                      → {normalizeReviewDisplayText(item.recommendation)}
                    </p>
                  )}
                </button>
                {showReviewerJudgments && (
                  <div className="mx-3 mb-3 space-y-1 rounded border border-gray-100 bg-gray-50 p-2">
                    {judgments.length > 0 ? (
                      judgments.map((j) => (
                        <div key={`${item.feedback_key}-${j.reviewer_user_id}`} className="text-xs">
                          <div className="flex items-center gap-2">
                            <JudgmentBadge judgment={j.judgment} />
                            <span className="font-medium text-gray-700">{j.reviewer_name}</span>
                          </div>
                          {j.comment_body && (
                            <p
                              className={`mt-1 leading-snug text-gray-600 ${
                                isSelected ? 'whitespace-pre-wrap break-words' : 'line-clamp-2'
                              }`}
                            >
                              {normalizeReviewDisplayText(j.comment_body)}
                            </p>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-gray-400">判定はまだありません</p>
                    )}
                  </div>
                )}
                {item.reason && (
                  <details className="-mt-1 px-3 pb-3">
                    <summary className="cursor-pointer list-none text-[11px] text-gray-500 hover:text-gray-700">
                      詳細を表示
                    </summary>
                    <p className="mt-1 text-xs leading-snug text-gray-600">
                      {normalizeReviewDisplayText(item.reason)}
                    </p>
                  </details>
                )}
                {canEditReviews && (
                  <div className="mx-3 mb-3 rounded-lg border border-gray-200 bg-white p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-gray-600">このAIフィードバックへの確認</span>
                      {draft.judgment && (
                        <span className="rounded bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                          {judgmentDisplayLabel(draft.judgment)} 選択中
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {(['OK', 'NG'] as const).map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => onItemJudgmentChange?.(item.feedback_key, value)}
                          className={`flex-1 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                            draft.judgment === value
                              ? value === 'NG'
                                ? 'border-red-600 bg-red-600 text-white'
                                : 'border-green-600 bg-green-600 text-white'
                              : value === 'NG'
                                ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                                : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                          }`}
                        >
                          {judgmentDisplayLabel(value)}
                        </button>
                      ))}
                    </div>
                    <textarea
                      placeholder="任意コメント"
                      value={draft.comment}
                      onChange={(e) => onItemCommentChange?.(item.feedback_key, e.target.value)}
                      onBlur={() => onItemCommentCommit?.(item.feedback_key)}
                      className="mt-2 w-full resize-none rounded-md border border-gray-300 p-2 text-xs leading-relaxed focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      rows={3}
                    />
                  </div>
                )}
              </div>
            );
          })}
          {reviewCards.length === 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">
              {showAiFeedback ? '検知されたフィードバックはありません' : 'フィードバックはありません'}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
